import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  /* =========================================================
     ACTIVE NAVIGATION
  ========================================================= */

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/" && !location.hash;
    }

    if (path.startsWith("/#")) {
      return (
        location.pathname === "/" &&
        location.hash === path.substring(1)
      );
    }

    if (path === "/case-studies") {
      return (
        location.pathname === "/case-studies" ||
        location.pathname.startsWith("/case-studies/")
      );
    }

    return location.pathname === path;
  };

  /* =========================================================
     CLOSE MOBILE MENU
  ========================================================= */

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const serviceLinks = [
    { title: "Custom Software Development", to: "/services/custom-software-development" },
    { title: "ERP & Business Management", to: "/services/erp-business-management" },
    { title: "CRM & Lead Management", to: "/services/crm-lead-management" },
    { title: "Healthcare Software", to: "/services/healthcare-software" },
    { title: "Inventory Management", to: "/services/inventory-management" },
    { title: "Industrial Process Software", to: "/services/industrial-process-software" },
    { title: "License Management", to: "/services/license-management" },
    { title: "Mobile App Development", to: "/services/mobile-app-development" },
  ];

  /* =========================================================
     CLOSE MENU WHEN ROUTE CHANGES
  ========================================================= */

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  /* =========================================================
     BODY SCROLL LOCK FOR MOBILE MENU
  ========================================================= */

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("mobile-menu-open");
    } else {
      document.body.classList.remove("mobile-menu-open");
    }

    return () => {
      document.body.classList.remove("mobile-menu-open");
    };
  }, [menuOpen]);

  return (
    <header className="site-header">
      <div className="header-inner">

        {/* =====================================================
            BRAND
        ====================================================== */}

        <Link
          to="/"
          className="brand"
          onClick={closeMenu}
          aria-label="HexxaTech Softwares - Custom Software Development Company in Pune"
        >
          <span className="brand-name">
            HEXXATECH
          </span>

          <span className="brand-tagline">
            WHERE INNOVATION MEETS EXPERIENCE
          </span>
        </Link>

        {/* =====================================================
            DESKTOP / MOBILE NAVIGATION
        ====================================================== */}

        <nav
          id="main-navigation"
          className={`main-navigation ${
            menuOpen ? "mobile-open" : ""
          }`}
          aria-label="Primary navigation"
        >

          {/* HOME */}

          <Link
            to="/"
            className={
              isActive("/")
                ? "nav-link active"
                : "nav-link"
            }
            onClick={closeMenu}
            aria-current={
              isActive("/") ? "page" : undefined
            }
          >
            Home
          </Link>

          {/* CASE STUDIES */}

          <Link
            to="/case-studies"
            className={
              isActive("/case-studies")
                ? "nav-link active"
                : "nav-link"
            }
            onClick={closeMenu}
            aria-current={
              isActive("/case-studies")
                ? "page"
                : undefined
            }
          >
            Case Studies
          </Link>

          {/* ABOUT */}

          <Link
            to="/#about"
            className={
              isActive("/#about")
                ? "nav-link active"
                : "nav-link"
            }
            onClick={closeMenu}
            aria-current={
              isActive("/#about")
                ? "location"
                : undefined
            }
          >
            About
          </Link>

          {/* SERVICES */}

          <div className="nav-services-wrapper">
            <Link
              to="/#services"
              className={
                isActive("/#services")
                  ? "nav-link active"
                  : "nav-link"
              }
              onClick={closeMenu}
              aria-current={
                isActive("/#services")
                  ? "location"
                  : undefined
              }
            >
              Services
            </Link>

            <div className="nav-service-dropdown" aria-label="HexxaTech services menu">
              {serviceLinks.map((service) => (
                <Link
                  key={service.to}
                  to={service.to}
                  className="nav-service-item"
                  onClick={closeMenu}
                >
                  {service.title}
                </Link>
              ))}
            </div>
          </div>

          {/* HOW IT WORKS */}

          <Link
            to="/#how-it-works"
            className={
              isActive("/#how-it-works")
                ? "nav-link active"
                : "nav-link"
            }
            onClick={closeMenu}
            aria-current={
              isActive("/#how-it-works")
                ? "location"
                : undefined
            }
          >
            How It Works
          </Link>

          {/* CONTACT */}

          <Link
            to="/#contact"
            className={
              isActive("/#contact")
                ? "nav-link active"
                : "nav-link"
            }
            onClick={closeMenu}
            aria-current={
              isActive("/#contact")
                ? "location"
                : undefined
            }
          >
            Contact
          </Link>

        </nav>

        {/* =====================================================
            DESKTOP WHATSAPP CTA
        ====================================================== */}

        <a
          href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20a%20software%20project."
          target="_blank"
          rel="noopener noreferrer"
          className="header-whatsapp"
          aria-label="Chat with HexxaTech Softwares on WhatsApp"
          title="Chat with HexxaTech Softwares on WhatsApp"
          onClick={closeMenu}
        >
          <i
            className="icofont-brand-whatsapp"
            aria-hidden="true"
          />

          <span>
            Let's Talk
          </span>
        </a>

        {/* =====================================================
            MOBILE MENU BUTTON
        ====================================================== */}

        <button
          type="button"
          className={`mobile-menu-button ${
            menuOpen ? "open" : ""
          }`}
          onClick={() => setMenuOpen((previous) => !previous)}
          aria-label={
            menuOpen
              ? "Close navigation menu"
              : "Open navigation menu"
          }
          aria-expanded={menuOpen}
          aria-controls="main-navigation"
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </button>

      </div>
    </header>
  );
}